/**
 * Role clock mode: day stamp vs per-appointment stamp.
 * Stored on `instance_role_presets.clock_mode`.
 *
 * Wave 2: `activity` is selectable. Apply migration
 * `20260824210000_clock_activity_partial_uniques.sql` before deploying this
 * build — the day unique must already be gone.
 */
export const CLOCK_MODES = ['day', 'activity'] as const;
export type ClockMode = (typeof CLOCK_MODES)[number];

export const DEFAULT_CLOCK_MODE: ClockMode = 'day';

/** Wave 2: per-appointment stamps. Requires the partial-unique migration. */
export const CLOCK_ACTIVITY_MODE_AVAILABLE = true;

export function isClockMode(value: unknown): value is ClockMode {
  return value === 'day' || value === 'activity';
}

export function parseClockMode(value: unknown): ClockMode {
  return isClockMode(value) ? value : DEFAULT_CLOCK_MODE;
}

/**
 * Owner/admin capabilities ignore any leftover preset (synthetic "Vollzugriff").
 * Stempelmodus follows the same rule: inherit Büro Vollzugriff even if
 * `role_preset_id` is still set (invite or ownership transfer).
 */
export function clockModeForMembership(input: {
  hasAssignedPreset: boolean;
  assignedPresetMode: unknown;
  membershipRole: 'owner' | 'admin' | 'member';
  officeFullAccessMode: unknown;
}): ClockMode {
  if (input.membershipRole === 'owner' || input.membershipRole === 'admin') {
    return parseClockMode(input.officeFullAccessMode);
  }
  if (input.hasAssignedPreset) return parseClockMode(input.assignedPresetMode);
  return DEFAULT_CLOCK_MODE;
}

/** Unknown values → day. `activity` is allowed while CLOCK_ACTIVITY_MODE_AVAILABLE. */
export function parseClockModeForWrite(value: unknown): ClockMode | { error: string } {
  if (value === undefined || value === null || value === '') return DEFAULT_CLOCK_MODE;
  if (!isClockMode(value)) {
    return { error: 'clock_mode muss day oder activity sein.' };
  }
  if (value === 'activity' && !CLOCK_ACTIVITY_MODE_AVAILABLE) {
    return { error: 'Stempeln je Termin ist noch nicht freigeschaltet.' };
  }
  return value;
}
